import {
  Col,
  Form,
  Input,
  Modal,
  ModalProps,
  notification,
  Rate,
  Row,
  Select,
} from 'antd';
import { useIntl } from 'react-intl';
import { useEffect } from 'react';
import { isEmpty } from 'lodash';
import { NamePath } from 'antd/lib/form/interface';
import {
  Question,
  QuestionFormValues,
  QuestionTypeEnum,
} from '@/core/domain/crm/Questions/Questions.types';
import { FloatingFormItem } from '@/components/_common';
import MultipleChoices from './MultipleChoices';

interface Props extends ModalProps {
  data?: Question;
  onCreateQuestion?: (values: QuestionFormValues) => void;
  onChangeQuestion?: (values: QuestionFormValues) => void;
  onCancel?: (e?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
}

const SearchQuestionnaireQuestionModal: React.FC<Props> = ({
  data,
  onCreateQuestion,
  onChangeQuestion,
  ...props
}) => {
  const [form] = Form.useForm<QuestionFormValues>();
  const intl = useIntl();

  useEffect(() => {
    if (!!data && !isEmpty(data)) {
      form.setFieldsValue({
        type: data.type,
        description: data.description,
        optionsAnswer: data.optionsAnswer,
      });
    }
  }, [data]);

  const onModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (data && onChangeQuestion) {
        onChangeQuestion({ ...data, ...values });
      } else if (onCreateQuestion) {
        onCreateQuestion(values);
      }

      handleOnCancel();
    } catch (error) {
      notification.error({ message: 'error message' });
    }
  };

  const handleOnCancel = (e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
    form.resetFields();

    if (props.onCancel) props.onCancel(e);
  };

  const renderClassification = () => {
    return (
      <>
        <Row style={{ marginBottom: 24, marginTop: 24 }} gutter={[24, 24]}>
          <Col flex={1}>
            <FloatingFormItem name="lowerEnd" label="Extremidade inferior">
              <Input />
            </FloatingFormItem>
          </Col>
          <Col flex={1}>
            <FloatingFormItem name="topEnd" label="Extremidade superior">
              <Input />
            </FloatingFormItem>
          </Col>
        </Row>
        <Form.Item label="Exemplo" noStyle shouldUpdate>
          {({ getFieldValue }) => (
            <>
              {getFieldValue('lowerEnd')}
              <Rate style={{ marginLeft: 10, marginRight: 10 }} />
              {getFieldValue('topEnd')}
            </>
          )}
        </Form.Item>
      </>
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderExtraOptions = (getFieldValue: (name: NamePath) => any) => {
    switch (getFieldValue('type')) {
      case 'COMMENTS_BOX':
        return null;
      case 'MULTIPLE_CHOICE_MULTIPLE_ANSWERS':
        return (
          <MultipleChoices
            form={form}
            optionsAnswer={getFieldValue('optionsAnswer')}
          />
        );
      case 'MULTIPLE_CHOICE_SINGLE_ANSWER':
        return (
          <MultipleChoices
            form={form}
            optionsAnswer={getFieldValue('optionsAnswer')}
          />
        );
      case 'STARS_CLASSIFICATION':
        return renderClassification();
      default:
        return null;
    }
  };

  return (
    <Modal
      {...props}
      title={data ? 'Editar questão' : 'Adicionar questão'}
      okText="Salvar"
      onOk={onModalOk}
      onCancel={handleOnCancel}
      destroyOnClose
      width={900}
    >
      <Form form={form} layout="vertical">
        <Row gutter={[24, 24]}>
          <Col span={24} md={10}>
            <FloatingFormItem
              name="type"
              label="Tipo de questão"
              rules={[{ required: true }]}
            >
              <Select
                style={{ width: '100%' }}
                options={Object.values(QuestionTypeEnum).map((value) => ({
                  value,
                  label: intl.formatMessage({
                    id: `searchQuestionnaire.questions.list.type.${value}`,
                  }),
                }))}
              />
            </FloatingFormItem>
          </Col>
          <Col span={24} md={14}>
            <FloatingFormItem
              name="description"
              label="Descrição"
              rules={[{ required: true }]}
            >
              <Input />
            </FloatingFormItem>
          </Col>
        </Row>

        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => renderExtraOptions(getFieldValue)}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default SearchQuestionnaireQuestionModal;
